import { getAuthToken } from '@/lib/api';
import type { 
  ChatbotQueryRequest, 
  ChatbotQueryResponse, 
  HealthCheckResponse 
} from '@/types/chatbot';

const API_URL = import.meta.env.VITE_STREAM_API || 'http://localhost:5000/api';
const CHATBOT_API_URL = `${API_URL}/chatbot`;

/**
 * Chatbot REST API Service
 * Handles HTTP requests to the chatbot backend
 */

/**
 * Check chatbot service health and MongoDB connection
 * @returns Health status information
 */
export async function checkChatbotHealth(): Promise<HealthCheckResponse> {
  try {
    const response = await fetch(`${CHATBOT_API_URL}/health`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(`Health check failed: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error: any) {
    console.error('❌ Chatbot health check failed:', error);
    throw new Error(error.message || 'Chatbot service unavailable');
  }
}

/**
 * Query the chatbot with a question
 * @param groupId - Team/group identifier
 * @param memberId - User/member identifier
 * @param question - The question to ask
 * @param stream - Enable streaming response (requires WebSocket connection)
 * @returns Chatbot response
 */
export async function queryChatbot(
  groupId: string,
  memberId: string,
  question: string,
  stream: boolean = false
): Promise<ChatbotQueryResponse> {
  try {
    const token = getAuthToken();
    
    const requestBody: ChatbotQueryRequest = {
      groupId,
      memberId,
      question,
      stream,
    };

    const response = await fetch(`${CHATBOT_API_URL}/query`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
      body: JSON.stringify(requestBody),
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || data.error || 'Failed to query chatbot');
    }
    
    return data;
  } catch (error: any) {
    console.error('❌ Chatbot query failed:', error);
    
    // Provide user-friendly error messages
    if (error.message?.includes('MongoDB not initialized')) {
      throw new Error('The chatbot service is temporarily unavailable. Please try again later.');
    }
    
    if (error.message?.includes('Team not found')) {
      throw new Error('Team not found. Please check your team ID.');
    }
    
    if (error.message?.includes('OPENAI_API_KEY')) {
      throw new Error('AI service configuration error. Please contact support.');
    }
    
    if (error.message?.includes('Network') || error.message?.includes('fetch')) {
      throw new Error('Network error. Please check your internet connection.');
    }
    
    throw new Error(error.message || 'An unexpected error occurred. Please try again.');
  }
}

/**
 * Get chatbot configuration status
 * @returns Configuration status
 */
export async function getChatbotStatus(): Promise<{
  available: boolean;
  mongodb: boolean;
  streaming: boolean;
}> {
  try {
    const health = await checkChatbotHealth();
    return {
      available: health.status === 'healthy',
      mongodb: health.mongodb === 'connected',
      streaming: true, // WebSocket is always available if service is healthy
    };
  } catch (error) {
    return {
      available: false,
      mongodb: false,
      streaming: false,
    };
  }
}

/**
 * Handle chatbot API errors with user-friendly messages
 * @param error - Error object
 * @returns User-friendly error message
 */
export function handleChatbotError(error: any): string {
  if (error.message?.includes('MongoDB not initialized')) {
    return 'The chatbot service is temporarily unavailable. Please try again later.';
  }
  
  if (error.message?.includes('Team not found')) {
    return 'Team not found. Please check your team ID.';
  }
  
  if (error.message?.includes('OPENAI_API_KEY')) {
    return 'AI service configuration error. Please contact support.';
  }
  
  if (error.message?.includes('Network')) {
    return 'Network error. Please check your internet connection.';
  }
  
  return error.message || 'An unexpected error occurred. Please try again.';
}
